/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useTranslation } from 'react-i18next';
import { Anchor, Button, Text } from '@/shared/ui';
import { minAdDailyBudgetReais } from '@/shared/utils/ad-dialog-helpers';
import { AdDialogShell } from './AdDialogShell';
import type { AdErrorInfo } from './ad-dialog-types';

const ADS_MANAGER_URL = 'https://business.facebook.com/adsmanager/manage/accounts';

/**
 * Explains, in a dialog the user cannot miss, why a Lead Ad or Boost was not
 * created: Meta's own reason when it gave one, plus the steps that make the
 * next attempt work (the ad account needs a payment method, a confirmed Page
 * and a verified phone before Meta accepts any campaign).
 */
export function AdErrorModal({ error, onClose }: { error: AdErrorInfo; onClose: () => void }) {
  const { t } = useTranslation('features/properties');
  const isBoost = error.kind === 'boost';
  const { context, steps } = describeError(error, t);

  return (
    <AdDialogShell
      titleId="ad-error-title"
      title={isBoost ? t('adDialogs.error.boostTitle') : t('adDialogs.error.leadAdTitle')}
      onDismiss={onClose}
      testId="ad-error-modal"
    >
      <Text className="m-0 text-[length:var(--text-body-sm)] leading-[var(--leading-normal)] text-[var(--text-secondary)]">{context}</Text>
      <div className="flex flex-col gap-(--space-2)">
        <Text className="m-0 text-[length:var(--text-body-sm)] [font-weight:var(--font-semibold)] leading-[var(--leading-snug)] text-[var(--text-primary)]">{t('adDialogs.error.howToFixTitle')}</Text>
        <div className="flex flex-col gap-(--space-2)">
          {steps.map((step, index) => (
            <Text key={step} className="m-0 text-[length:var(--text-small)] leading-[var(--leading-normal)] text-[var(--text-secondary)]">{`${index + 1}. ${step}`}</Text>
          ))}
        </div>
      </div>
      <div className="flex items-center justify-between gap-(--space-3) pt-(--space-1)">
        <Anchor
          href={ADS_MANAGER_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[length:var(--text-small)] [font-weight:var(--font-semibold)] text-[var(--seed-base)] underline"
          data-testid="ad-error-open-ads-manager"
        >
          {t('adDialogs.error.openAdsManager')}
        </Anchor>
        <Button type="button" size="sm" onClick={onClose} data-testid="ad-error-close">
          {t('adDialogs.error.close')}
        </Button>
      </div>
    </AdDialogShell>
  );
}

/**
 * The explanation and the steps to follow for a failed ad: tailored when the
 * Lead Ad could not even be attempted (no ad account / Facebook not
 * connected) or the Page has not accepted Meta's Lead Generation Terms,
 * otherwise the generic "ad account still needs setup" guidance.
 */
function describeError(
  error: AdErrorInfo,
  t: (key: string, opts?: Record<string, unknown>) => string,
): { context: string; steps: string[] } {
  const known = error.code ? ERROR_COPY[error.code] : undefined;
  if (known) {
    return {
      context: t(`adDialogs.error.${known.context}`),
      steps: known.steps.map((step) => t(`adDialogs.error.${step}`, { min: minAdDailyBudgetReais() })),
    };
  }
  return {
    context: error.kind === 'boost' ? t('adDialogs.error.boostContext') : t('adDialogs.error.leadAdContext'),
    steps: [t('adDialogs.error.step1'), t('adDialogs.error.step2'), t('adDialogs.error.step3')],
  };
}

/** The i18n keys (under adDialogs.error) of the guidance for each recognized failure. */
const ERROR_COPY: Record<NonNullable<AdErrorInfo['code']>, { context: string; steps: string[] }> = {
  budget_below_minimum: {
    context: 'budgetTooLowContext',
    steps: ['budgetTooLowStep1', 'budgetTooLowStep2', 'budgetTooLowStep3'],
  },
  leadgen_terms_not_accepted: {
    context: 'leadGenTermsContext',
    steps: ['leadGenTermsStep1', 'leadGenTermsStep2', 'leadGenTermsStep3'],
  },
  ad_account_missing: {
    context: 'noAdAccountContext',
    steps: ['setupStep1', 'setupStep2', 'setupStep3'],
  },
  facebook_not_connected: {
    context: 'facebookNotConnectedContext',
    steps: ['setupStep1', 'setupStep2', 'setupStep3'],
  },
  form_question_prohibited: {
    context: 'formQuestionProhibitedContext',
    steps: ['formQuestionProhibitedStep1', 'formQuestionProhibitedStep2', 'formQuestionProhibitedStep3'],
  },
};
